import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PortalModal from '@/components/ui/PortalModal';
import { 
  Building2, Search, Edit2, Trash2, X, Save, Loader2, 
  Users, Palette, Link, Upload, ExternalLink, Eye, EyeOff,
  UserPlus, ChevronDown, ChevronUp, Check, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PartnerManagement() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [athleteSearch, setAthleteSearch] = useState('');
  
  // New partner form state
  const [newPartner, setNewPartner] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    organization_name: '',
    slug: ''
  });
  
  // Edit partner form state
  const [editPartner, setEditPartner] = useState({
    full_name: '',
    email: '',
    phone: '',
    organization_name: '',
    primary_color: '#02b5ff',
    secondary_color: '#1a1a2e',
    accent_color: '#ff025f',
    welcome_message: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whitelabel/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data.partners || []);
    } catch (err) {
      console.error('Failed to load partners:', err);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAthletes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAthletes(response.data || []);
    } catch (err) {
      console.error('Failed to load athletes:', err);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
    fetchAthletes();
  }, [fetchPartners, fetchAthletes]);

  const filteredPartners = partners.filter(partner => {
    const matchesSearch = !searchQuery || 
      partner.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const createPartner = async () => {
    if (!newPartner.full_name?.trim()) {
      toast.error('Contact name is required');
      return;
    }
    if (!newPartner.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!newPartner.organization_name?.trim()) {
      toast.error('Organization name is required');
      return;
    }
    if (!newPartner.slug?.trim()) {
      toast.error('URL slug is required');
      return;
    }
    if (!newPartner.password || newPartner.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Validate slug format
    const slugPattern = /^[a-z0-9-]+$/;
    if (!slugPattern.test(newPartner.slug.toLowerCase())) {
      toast.error('URL slug can only contain lowercase letters, numbers, and hyphens');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whitelabel/partners/create`, {
        full_name: newPartner.full_name.trim(),
        email: newPartner.email.trim().toLowerCase(),
        phone: newPartner.phone?.trim() || '',
        password: newPartner.password,
        organization_name: newPartner.organization_name.trim(),
        slug: newPartner.slug.toLowerCase().trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Partner "${newPartner.organization_name}" created successfully`);
      setShowAddModal(false);
      setNewPartner({ full_name: '', email: '', phone: '', password: '', organization_name: '', slug: '' });
      fetchPartners();
    } catch (err) {
      console.error('Create partner error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create partner');
    } finally {
      setSaving(false);
    }
  };

  const deletePartner = async (partner) => {
    if (!confirm(`Are you sure you want to delete "${partner.organization_name}"? This will remove the partner account and their branding, but athletes will remain in the system.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/whitelabel/partners/${partner.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner deleted successfully');
      fetchPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete partner');
    }
  };

  const openAssignModal = (partner) => {
    setSelectedPartner(partner);
    // Pre-select athletes already assigned to this partner
    const assignedIds = athletes
      .filter(a => a.partner_id === partner.id)
      .map(a => a.id);
    setSelectedAthletes(assignedIds);
    setShowAssignModal(true);
  };

  const openEditModal = (partner) => {
    setSelectedPartner(partner);
    setEditPartner({
      full_name: partner.full_name || '',
      email: partner.email || '',
      phone: partner.phone || '',
      organization_name: partner.organization_name || '',
      primary_color: partner.brand?.primary_color || '#02b5ff',
      secondary_color: partner.brand?.secondary_color || '#1a1a2e',
      accent_color: partner.brand?.accent_color || '#ff025f',
      welcome_message: partner.brand?.welcome_message || ''
    });
    setShowEditModal(true);
  };

  const updatePartner = async () => {
    if (!selectedPartner) return;
    
    if (!editPartner.full_name?.trim()) {
      toast.error('Contact name is required');
      return;
    }
    if (!editPartner.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!editPartner.organization_name?.trim()) {
      toast.error('Organization name is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/whitelabel/partners/${selectedPartner.id}`, {
        full_name: editPartner.full_name.trim(),
        email: editPartner.email.trim().toLowerCase(),
        phone: editPartner.phone?.trim() || '',
        organization_name: editPartner.organization_name.trim(),
        primary_color: editPartner.primary_color,
        secondary_color: editPartner.secondary_color,
        accent_color: editPartner.accent_color,
        welcome_message: editPartner.welcome_message?.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Partner "${editPartner.organization_name}" updated successfully`);
      setShowEditModal(false);
      setSelectedPartner(null);
      fetchPartners();
    } catch (err) {
      console.error('Update partner error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update partner');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPartner) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, SVG, or WebP');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/whitelabel/partners/${selectedPartner.id}/upload-logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded successfully');
      fetchPartners();
      // Update selected partner's brand logo
      const response = await axios.get(`${API}/whitelabel/partners/${selectedPartner.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPartner(prev => ({
        ...prev,
        brand: response.data.brand
      }));
    } catch (err) {
      console.error('Logo upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const deleteLogo = async () => {
    if (!selectedPartner) return;
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/whitelabel/partners/${selectedPartner.id}/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logo deleted');
      fetchPartners();
      setSelectedPartner(prev => ({
        ...prev,
        brand: { ...prev.brand, logo_url: null }
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const toggleAthleteSelection = (athleteId) => {
    setSelectedAthletes(prev => 
      prev.includes(athleteId) 
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const saveAthleteAssignments = async () => {
    if (!selectedPartner) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get currently assigned athletes
      const currentlyAssigned = athletes
        .filter(a => a.partner_id === selectedPartner.id)
        .map(a => a.id);
      
      // Athletes to assign (newly selected)
      const toAssign = selectedAthletes.filter(id => !currentlyAssigned.includes(id));
      
      // Athletes to unassign (previously assigned but now deselected)
      const toUnassign = currentlyAssigned.filter(id => !selectedAthletes.includes(id));
      
      // Bulk assign new athletes
      if (toAssign.length > 0) {
        await axios.post(`${API}/whitelabel/partners/${selectedPartner.id}/bulk-assign`, 
          toAssign,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Unassign removed athletes
      for (const childId of toUnassign) {
        await axios.post(`${API}/whitelabel/partners/${selectedPartner.id}/unassign-athlete/${childId}`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success(`Athletes updated for ${selectedPartner.organization_name}`);
      setShowAssignModal(false);
      fetchPartners();
      fetchAthletes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update athlete assignments');
    } finally {
      setSaving(false);
    }
  };

  const filteredAthletes = athletes.filter(athlete => {
    const name = `${athlete.first_name} ${athlete.last_name}`.toLowerCase();
    return name.includes(athleteSearch.toLowerCase());
  });

  return (
    <div className="space-y-4" data-testid="partner-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
            <Building2 className="h-5 w-5 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Partner Organizations</h2>
            <p className="text-xs text-zinc-500">{partners.length} partners • White-label management</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
          data-testid="add-partner-btn"
        >
          <Building2 className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search partners..."
          className="pl-10 bg-zinc-800 border-zinc-700 text-white"
          data-testid="partner-search"
        />
      </div>

      {/* Partner List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No partners found</p>
          <p className="text-sm mt-1">Create a partner to enable white-label branding</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPartners.map((partner) => (
            <div
              key={partner.id}
              className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-indigo-500/50 transition-all"
              data-testid={`partner-card-${partner.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  {/* Logo/Avatar */}
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: partner.brand?.primary_color || '#6366f1' }}
                  >
                    {partner.brand?.logo_url ? (
                      <img 
                        src={`${API}${partner.brand.logo_url}`} 
                        alt={partner.organization_name}
                        className="w-full h-full object-contain rounded-xl"
                      />
                    ) : (
                      partner.organization_name?.charAt(0) || 'P'
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-white">{partner.organization_name}</h3>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-500/20 text-indigo-300">
                        Partner
                      </span>
                    </div>
                    <p className="text-sm text-zinc-400">{partner.full_name} • {partner.email}</p>
                    <div className="flex items-center gap-3 mt-2">
                      {partner.brand?.slug && (
                        <span className="flex items-center gap-1 text-xs text-zinc-500">
                          <Globe className="h-3 w-3" />
                          /login/{partner.brand.slug}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-zinc-500">
                        <Users className="h-3 w-3" />
                        {partner.athlete_count || 0} athletes
                      </span>
                      {partner.brand?.primary_color && (
                        <div className="flex items-center gap-1">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: partner.brand.primary_color }}
                          />
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: partner.brand.secondary_color }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(partner)}
                    className="text-zinc-400 hover:text-indigo-400 hover:bg-indigo-500/10"
                    title="Edit Partner"
                    data-testid={`edit-partner-${partner.id}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openAssignModal(partner)}
                    className="text-zinc-400 hover:text-indigo-400 hover:bg-indigo-500/10"
                    title="Assign Athletes"
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePartner(partner)}
                    className="text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                    title="Delete Partner"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Partner Modal */}
      <PortalModal isOpen={showAddModal} onClose={() => setShowAddModal(false)}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-indigo-400" />
            <h3 className="text-lg font-bold text-white">Add Partner Organization</h3>
          </div>
          <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-3 mb-4">
            <p className="text-sm text-indigo-200">
              Partners get their own branded login page and can only see athletes assigned to them.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm text-zinc-400 mb-1 block">Organization Name *</label>
              <Input
                value={newPartner.organization_name}
                onChange={(e) => setNewPartner({ ...newPartner, organization_name: e.target.value })}
                placeholder="Future.fit Football"
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="partner-org-name"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm text-zinc-400 mb-1 block">URL Slug *</label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">/login/</span>
                <Input
                  value={newPartner.slug}
                  onChange={(e) => setNewPartner({ ...newPartner, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="futurefit"
                  className="bg-zinc-800 border-zinc-700 text-white flex-1"
                  data-testid="partner-slug"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Only lowercase letters, numbers, and hyphens</p>
            </div>
          </div>
          
          <div className="border-t border-zinc-700 pt-4">
            <h4 className="text-sm font-medium text-white mb-3">Contact Person</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Full Name *</label>
                <Input
                  value={newPartner.full_name}
                  onChange={(e) => setNewPartner({ ...newPartner, full_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="partner-name"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={newPartner.email}
                  onChange={(e) => setNewPartner({ ...newPartner, email: e.target.value })}
                  placeholder="john@futurefit.com"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="partner-email"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Phone</label>
                <Input
                  value={newPartner.phone}
                  onChange={(e) => setNewPartner({ ...newPartner, phone: e.target.value })}
                  placeholder="07123456789"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="partner-phone"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPartner.password}
                    onChange={(e) => setNewPartner({ ...newPartner, password: e.target.value })}
                    placeholder="Min 6 characters"
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    data-testid="partner-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            className="flex-1 border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            onClick={createPartner}
            disabled={saving || !newPartner.full_name || !newPartner.email || !newPartner.organization_name || !newPartner.slug || newPartner.password?.length < 6}
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Building2 className="h-4 w-4 mr-2" />}
            Create Partner
          </Button>
        </div>
      </PortalModal>

      {/* Edit Partner Modal */}
      <PortalModal isOpen={showEditModal} onClose={() => setShowEditModal(false)}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Edit2 className="h-5 w-5 text-indigo-400" />
            <h3 className="text-lg font-bold text-white">Edit Partner</h3>
          </div>
          <button onClick={() => setShowEditModal(false)} className="text-zinc-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {/* Logo Upload Section */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <label className="text-sm font-medium text-white mb-3 block">Organization Logo</label>
            <div className="flex items-center gap-4">
              <div 
                className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-zinc-600"
                style={{ backgroundColor: editPartner.primary_color || '#6366f1' }}
              >
                {selectedPartner?.brand?.logo_url ? (
                  <img 
                    src={`${API}${selectedPartner.brand.logo_url}`} 
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Upload className="h-8 w-8 text-white/50" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('logo-upload').click()}
                  disabled={uploadingLogo}
                  className="border-zinc-600 w-full"
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  {selectedPartner?.brand?.logo_url ? 'Change Logo' : 'Upload Logo'}
                </Button>
                {selectedPartner?.brand?.logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deleteLogo}
                    disabled={uploadingLogo}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Logo
                  </Button>
                )}
                <p className="text-xs text-zinc-500">PNG, JPG, SVG or WebP. Max 5MB.</p>
              </div>
            </div>
          </div>
          
          {/* Brand Colors */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
            <label className="text-sm font-medium text-white mb-3 block flex items-center gap-2">
              <Palette className="h-4 w-4 text-indigo-400" />
              Brand Colors
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Primary</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editPartner.primary_color}
                    onChange={(e) => setEditPartner({ ...editPartner, primary_color: e.target.value })}
                    className="w-10 h-10 rounded-lg border border-zinc-600 cursor-pointer bg-transparent"
                  />
                  <Input
                    value={editPartner.primary_color}
                    onChange={(e) => setEditPartner({ ...editPartner, primary_color: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1"
                    placeholder="#02b5ff"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Secondary</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editPartner.secondary_color}
                    onChange={(e) => setEditPartner({ ...editPartner, secondary_color: e.target.value })}
                    className="w-10 h-10 rounded-lg border border-zinc-600 cursor-pointer bg-transparent"
                  />
                  <Input
                    value={editPartner.secondary_color}
                    onChange={(e) => setEditPartner({ ...editPartner, secondary_color: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1"
                    placeholder="#1a1a2e"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Accent</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={editPartner.accent_color}
                    onChange={(e) => setEditPartner({ ...editPartner, accent_color: e.target.value })}
                    className="w-10 h-10 rounded-lg border border-zinc-600 cursor-pointer bg-transparent"
                  />
                  <Input
                    value={editPartner.accent_color}
                    onChange={(e) => setEditPartner({ ...editPartner, accent_color: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white text-xs flex-1"
                    placeholder="#ff025f"
                  />
                </div>
              </div>
            </div>
            {/* Color Preview */}
            <div className="mt-3 flex gap-2">
              <div 
                className="flex-1 h-8 rounded-lg flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: editPartner.primary_color, color: '#fff' }}
              >
                Primary
              </div>
              <div 
                className="flex-1 h-8 rounded-lg flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: editPartner.secondary_color, color: '#fff' }}
              >
                Secondary
              </div>
              <div 
                className="flex-1 h-8 rounded-lg flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: editPartner.accent_color, color: '#fff' }}
              >
                Accent
              </div>
            </div>
          </div>
          
          {/* Organization Details */}
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Organization Name *</label>
            <Input
              value={editPartner.organization_name}
              onChange={(e) => setEditPartner({ ...editPartner, organization_name: e.target.value })}
              placeholder="Future.fit Football"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="edit-partner-org-name"
            />
          </div>
          
          {/* URL Slug (Read-only) */}
          {selectedPartner?.brand?.slug && (
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">URL Slug</label>
              <div className="flex items-center gap-2 bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2">
                <Globe className="h-4 w-4 text-zinc-500" />
                <span className="text-zinc-400 text-sm">/login/{selectedPartner.brand.slug}</span>
                <span className="ml-auto text-xs text-zinc-600">(cannot be changed)</span>
              </div>
            </div>
          )}
          
          {/* Contact Details */}
          <div className="border-t border-zinc-700 pt-4">
            <h4 className="text-sm font-medium text-white mb-3">Contact Person</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Full Name *</label>
                <Input
                  value={editPartner.full_name}
                  onChange={(e) => setEditPartner({ ...editPartner, full_name: e.target.value })}
                  placeholder="John Smith"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-partner-name"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={editPartner.email}
                  onChange={(e) => setEditPartner({ ...editPartner, email: e.target.value })}
                  placeholder="john@futurefit.com"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-partner-email"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Phone</label>
                <Input
                  value={editPartner.phone}
                  onChange={(e) => setEditPartner({ ...editPartner, phone: e.target.value })}
                  placeholder="07123456789"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-partner-phone"
                />
              </div>
            </div>
          </div>
          
          {/* Welcome Message */}
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Welcome Message (shown on login page)</label>
            <textarea
              value={editPartner.welcome_message}
              onChange={(e) => setEditPartner({ ...editPartner, welcome_message: e.target.value })}
              placeholder="Welcome to our performance tracking platform..."
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm resize-none"
              rows={3}
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowEditModal(false)}
            className="flex-1 border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            onClick={updatePartner}
            disabled={saving || !editPartner.full_name || !editPartner.email || !editPartner.organization_name}
            className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white disabled:opacity-50"
            data-testid="save-partner-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </PortalModal>

      {/* Assign Athletes Modal */}
      {showAssignModal && selectedPartner && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
        >
          <div 
            className="fixed inset-0 bg-black/95"
            onClick={() => setShowAssignModal(false)}
          />
          <div 
            className="relative bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg p-6 max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-indigo-400" />
                <div>
                  <h3 className="text-lg font-bold text-white">Assign Athletes</h3>
                  <p className="text-sm text-zinc-400">{selectedPartner.organization_name}</p>
                </div>
              </div>
              <button onClick={() => setShowAssignModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                value={athleteSearch}
                onChange={(e) => setAthleteSearch(e.target.value)}
                placeholder="Search athletes..."
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            {/* Selected count */}
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-zinc-400">{filteredAthletes.length} athletes</span>
              <span className="text-indigo-400">{selectedAthletes.length} selected</span>
            </div>
            
            {/* Athlete List */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px] max-h-[400px]">
              {filteredAthletes.map((athlete) => {
                const isSelected = selectedAthletes.includes(athlete.id);
                const isAssignedToOther = athlete.partner_id && athlete.partner_id !== selectedPartner.id;
                
                return (
                  <button
                    key={athlete.id}
                    onClick={() => !isAssignedToOther && toggleAthleteSelection(athlete.id)}
                    disabled={isAssignedToOther}
                    className={`w-full p-3 rounded-lg border transition-all text-left flex items-center justify-between ${
                      isSelected 
                        ? 'border-indigo-500 bg-indigo-500/10' 
                        : isAssignedToOther
                        ? 'border-zinc-700 bg-zinc-800/30 opacity-50 cursor-not-allowed'
                        : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-white">{athlete.first_name} {athlete.last_name}</p>
                      <p className="text-xs text-zinc-500">
                        {athlete.current_team_name || 'No team'}
                        {isAssignedToOther && ` • Assigned to ${athlete.partner_name || 'another partner'}`}
                      </p>
                    </div>
                    {isSelected && <Check className="h-5 w-5 text-indigo-400" />}
                  </button>
                );
              })}
            </div>
            
            <div className="flex gap-3 mt-4 pt-4 border-t border-zinc-700">
              <Button
                variant="outline"
                onClick={() => setShowAssignModal(false)}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={saveAthleteAssignments}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Save Assignments
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
